import {
  freshBooking,
  eventIsPrivateFor,
  currentUser,
  fetchEventDetails,
  inferRecurrence
} from '../../functions/calendar/calendarUtils.js'

// For recurring events, inherit missing fields from a sibling instance
const _inheritFromSeries = (ev, events) => {
  if (!ev || (ev.invitees?.length && ev.description !== undefined)) return ev
  const baseId = ev.googleEventId?.replace(/_\d{8}T\d{6}Z$/, '')
  if (!baseId) return ev
  const sibling = (events || []).find(e =>
    e.id !== ev.id && e.googleEventId?.replace(/_\d{8}T\d{6}Z$/, '') === baseId &&
    (e.invitees?.length || e.description)
  )
  if (!sibling) return ev
  return {
    ...ev,
    invitees: ev.invitees?.length ? ev.invitees : sibling.invitees,
    description: ev.description ?? sibling.description,
    type: ev.type || sibling.type
  }
}

const _clearEventHash = () => {
  if (typeof window === 'undefined') return
  const hash = window.location.hash || ''
  if (hash.includes('event=')) {
    const next = '#view=' + (hash.match(/view=([^&]*)/)?.[1] || 'week')
    window.history.replaceState(null, '', next)
  }
}

export const CalEventDetailModal = {
  extends: 'Modal',
  stateKey: 'showEventDetail',
  extraClose: { selectedEvent: null },

  display: (el, s) => {
    const show = s.root?.showEventDetail
    if (show) {
      const ev = s.root?.selectedEvent
      if (ev && ev.invitees === undefined) {
        // Try inheriting from a sibling immediately for instant display
        const enriched = _inheritFromSeries(ev, s.root?.events)
        if (enriched !== ev && enriched.invitees?.length) {
          s.root.update({ selectedEvent: enriched })
        }
        // Also fire the lazy-load for this specific instance
        if (el._lastDetailId !== ev.id) {
          el._lastDetailId = ev.id
          fetchEventDetails(ev.id, s.root)
        }
      }
      return 'flex'
    }
    if (el._lastDetailId) {
      el._lastDetailId = null
      _clearEventHash()
    }
    return 'none'
  },

  Window: {
    width: '100%',
    maxWidth: 'I', // ~800px
    maxHeight: '90dvh',
    ModalHeader: {
      Hgroup: {
        H: {
          text: (el, s) => {
            const ev = s.root?.selectedEvent
            if (!ev) return ''
            if (eventIsPrivateFor(ev, currentUser(s.root))) return 'Meeting'
            return ev.title || ''
          }
        },
        P: {
          text: null,
          flow: 'x',
          align: 'center left',
          gap: 'Z1',
          Icon: { icon: 'clock' },
          Text: {
            tag: 'span',
            text: (el, s) => {
              const ev = s.root?.selectedEvent
              if (!ev) return ''
              return `${ev.date} · ${ev.startTime} – ${ev.endTime}`
            }
          }
        }
      },
      Icons: {
        EditBtn: {
          extends: 'IconButton',
          Icon: { icon: 'edit' },
          onClick: (e, el, s) => {
            const raw = s.root?.selectedEvent
            if (!raw) return
            const ev = _inheritFromSeries(raw, s.root?.events)
            // Infer recurrence from sibling instances for Google-synced events
            const inferred = inferRecurrence(s.root?.events, ev)
            // Detect meeting mode from existing event data
            const desc = ev.description || ''
            let meetingMode = ev.meetingMode || 'symbols-meet'
            let meetUrl = ev.meetUrl || ''
            if (!ev.meetingMode || ev.meetingMode === 'online') {
              if (desc.includes('/meet#room=')) meetingMode = 'symbols-meet'
              else if (ev.meetUrl || desc.match(/https?:\/\//)) {
                meetingMode = 'external-url'
                if (!meetUrl && desc.match(/https?:\/\/\S+/)) meetUrl = desc.match(/https?:\/\/\S+/)[0]
              } else meetingMode = 'symbols-meet'
            }
            s.root.update({
              showEventDetail: false,
              selectedEvent: null,
              showNewBooking: true,
              newBooking: freshBooking({
                title: ev.title,
                date: ev.date,
                startTime: ev.startTime,
                endTime: ev.endTime,
                startAt: ev.startAt,
                endAt: ev.endAt,
                invitees: (ev.invitees || []).join(', '),
                description: ev.description || '',
                type: ev.type || 'meeting',
                meetingMode,
                meetUrl,
                recurring: inferred.recurring,
                recurringDays: inferred.recurringDays,
                editingEventId: ev.id
              })
            })
          }
        },
        DeleteBtn: {
          extends: 'IconButton',
          Icon: { icon: 'trash' },
          onClick: (e, el, s) => {
            const ev = s.root?.selectedEvent
            if (!ev) return
            s.root.update({
              confirmAction: {
                title: `Delete "${ev.title || 'event'}"?`,
                message: 'This permanently removes the event from the calendar.',
                confirmText: 'Delete',
                icon: 'trash',
                onConfirm: () => {
                  s.root.update({
                    events: (s.root.events || []).filter((x) => x.id !== ev.id),
                    showEventDetail: false,
                    selectedEvent: null
                  })
                  el.call('deleteEventFromSupabase', ev.id)
                }
              }
            })
          }
        }
      }
    },

    Body: {
      flow: 'y',
      gap: 'A',

      // Linked resources — chat channel(s) / meet room(s) that reference
      // this event. Hidden when there are no links.
      Links: {
        extends: 'LinkedResources',
        type: 'calendar_event',
        id: (el, s) => s?.root?.selectedEvent?.id || null
      },

      // Edit/Delete actions are in the ModalFooter at the bottom of the Window.

      // Proposal banner — visible only when the open event is a pending
      // proposal. Lets the original creator (or any other invitee) accept,
      // which moves the original event to the proposed time and removes the
      // proposal duplicate, or decline, which just removes the proposal.
      ProposalBanner: {
        display: (el, s) =>
          s.root?.selectedEvent?.isProposal ? 'flex' : 'none',
        flow: 'x',
        align: 'center space-between',
        gap: 'Z',
        padding: 'Z A',
        round: 'Z',
        background: 'overlay',
        flexWrap: 'wrap',

        Text: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'title',
          flex: '1',
          text: (el, s) => {
            const ev = s.root?.selectedEvent
            if (!ev) return ''
            return `Proposed by ${ev.proposedBy || 'a participant'} — awaiting confirmation`
          }
        },
        Actions: {
          flow: 'x',
          gap: 'Y',
          DeclineBtn: {
            extends: 'Button',
            theme: 'secondary',
            text: 'Decline',
            onClick: (e, el, s) => {
              const ev = s.root?.selectedEvent
              if (!ev) return
              s.root.update({
                events: (s.root.events || []).filter((x) => x.id !== ev.id),
                showEventDetail: false,
                selectedEvent: null
              })
              el.call('deleteEventFromSupabase', ev.id)
            }
          },
          AcceptBtn: {
            extends: 'Button',
            theme: 'primary',
            text: 'Accept',
            onClick: (e, el, s) => {
              const ev = s.root?.selectedEvent
              if (!ev) return
              const events = s.root.events || []
              // Apply the proposal to the original and drop the duplicate.
              const newEvents = events
                .filter((x) => x.id !== ev.id)
                .map((x) =>
                  x.id === ev.originalEventId
                    ? {
                        ...x,
                        date: ev.date,
                        startTime: ev.startTime,
                        endTime: ev.endTime
                      }
                    : x
                )
              s.root.update({
                events: newEvents,
                showEventDetail: false,
                selectedEvent: null
              })
              // Persist: original event moves to the proposed time, the
              // proposal duplicate is deleted. Both push to Google.
              const movedOrig = newEvents.find(
                (x) => x.id === ev.originalEventId
              )
              if (movedOrig) el.call('saveEventToSupabase', movedOrig)
              el.call('deleteEventFromSupabase', ev.id)
            }
          }
        }
      },

      // Creator / organizer row — resolves the creatorEmail to the person's
      // display name (and avatar) via root.people, falls back to the raw
      // email if no profile is loaded.
      CreatorRow: {
        display: (el, s) => {
          const ev = s.root?.selectedEvent
          if (!ev?.creatorEmail) return 'none'
          if (eventIsPrivateFor(ev, currentUser(s.root))) return 'none'
          return 'flex'
        },
        flow: 'x',
        align: 'center left',
        gap: 'Z1',

        Avatar: {
          avatarType: 'initials',
          boxSize: 'B B',
          src: (el, s) => {
            const ev = s.root?.selectedEvent
            if (!ev) return el.call('avatarUrl', null)
            const creator = el.call('findPerson', ev.creatorEmail)
            return el.call('avatarUrl', (creator && creator.name) || ev.creatorEmail)
          }
        },
        Text: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'subtitle',
          text: (el, s) => {
            const ev = s.root?.selectedEvent
            if (!ev) return ''
            const creator = el.call('findPerson', ev.creatorEmail)
            return `Created by ${(creator && creator.name) || ev.creatorEmail}`
          }
        }
      },

      // Description and Invitees are lazy-loaded by fetchEventDetails when
      // the modal opens. While loading, both show a skeleton; once loaded,
      // empty/undefined fields hide normally.
      Description: {
        display: (el, s) => {
          const ev = s.root?.selectedEvent
          if (!ev) return 'none'
          if (eventIsPrivateFor(ev, currentUser(s.root))) return 'none'
          // Loading: description is undefined (not yet fetched)
          if (ev.description === undefined) return 'flex'
          // Loaded: only show if there's content
          return ev.description ? 'flex' : 'none'
        },
        flow: 'x',
        align: 'flex-start left',
        gap: 'Z1',

        Icon: { icon: 'menu', opacity: '.5' },

        Text: {
          tag: 'p',
          margin: '0',
          fontSize: 'Z2',
          color: 'subtitle',
          lineHeight: '1.6',
          text: (el, s) => s.root?.selectedEvent?.description || ''
        },

        Skeleton: {
          hide: (el, s) => s.root?.selectedEvent?.description !== undefined,
          flex: '1',
          height: 'A',
          round: 'V',
          background: 'overlay',
          opacity: '.6'
        }
      },

      InviteesSection: {
        display: (el, s) => {
          const ev = s.root?.selectedEvent
          if (!ev) return 'none'
          if (eventIsPrivateFor(ev, currentUser(s.root))) return 'none'
          // Loading: invitees is undefined
          if (ev.invitees === undefined) return 'flex'
          // Loaded: only show if there are invitees
          return ev.invitees.length ? 'flex' : 'none'
        },
        flow: 'y',
        gap: 'Z1',

        HeaderRow: {
          flow: 'x',
          align: 'center left',
          gap: 'Z1',

          Icon: { icon: 'user' },
          Label: {
            tag: 'span',
            fontSize: 'Z2',
            fontWeight: '600',
            color: 'title',
            text: (el, s) => {
              const ev = s.root?.selectedEvent
              if (!ev || ev.invitees === undefined) return 'Loading guests...'
              const count = ev.invitees.length
              return `${count} guest${count !== 1 ? 's' : ''}`
            }
          }
        },

        List: {
          hide: (el, s) => s.root?.selectedEvent?.invitees === undefined,
          flow: 'y',
          gap: 'Z',
          paddingLeft: 'C',

          childExtends: 'CalInviteeRow',
          childrenAs: 'state',
          children: (el, s) =>
            (s.root?.selectedEvent?.invitees || []).map((email) => ({ email }))
        },

        SkeletonList: {
          hide: (el, s) => s.root?.selectedEvent?.invitees !== undefined,
          flow: 'y',
          gap: 'Z',
          paddingLeft: 'C',

          Row1: {
            height: 'B',
            round: 'V',
            background: 'overlay',
            opacity: '.6'
          },
          Row2: {
            height: 'B',
            round: 'V',
            background: 'overlay',
            opacity: '.4'
          },
          Row3: {
            height: 'B',
            round: 'V',
            background: 'overlay',
            opacity: '.3'
          }
        }
      }
    },

    ModalFooter: {
      flow: 'x',
      gap: 'Z1',
      // Cross-app: launch a meet call linked to this calendar event. The
      // event id (bigint) flows into meet_rooms.calendar_event_id, which the
      // meet RLS policies treat as a normal owned room.
      SaveBtn: {
        extends: 'Button',
        theme: 'primary',
        text: 'Save event',
        onClick: async (e, el, s) => {
          const ev = s.root?.selectedEvent
          if (!ev) return
          // Reuse an existing meet_room for this event if present, otherwise
          // create a new one.
          const existing = (s.root.rooms || []).find(
            (r) => r.calendarEventId === ev.id
          )
          let id = existing?.id
          if (!id) {
            id = await el.call('createRoom', {
              name: ev.title || 'Calendar call',
              kind: 'scheduled',
              calendarEventId: ev.id,
              transcribeEnabled: true
            })
          }
          if (!id) return
          s.root.update({
            showEventDetail: false,
            selectedEvent: null,
            activeRoomId: id,
            meetView: 'call'
          })
          if (typeof window !== 'undefined') {
            window.location.hash = `room=${id}&view=call`
          }
          // Walk up to the page root with the `routes` prop — el.getRoot()
          // doesn't exist in DOMQL v3.14.
          let __root = el
          while (__root && !__root.routes) __root = __root.parent
          if (__root) el.router('/meet', __root)
          await el.call('connectCall', id)
        }
      },

      Spacer: { flex: '1' },

      AskAIBtn: {
        extends: 'Button',
        theme: 'secondary',
        flow: 'x',
        gap: 'Z',
        align: 'center center',
        Icon: { icon: 'magicstar', boxSize: 'Z2' },
        text: 'Ask AI',
        onClick: (e, el, s) => {
          const ev = s.root?.selectedEvent
          if (!ev) return
          s.root.update({
            assistantOpen: true,
            assistantAttachment: {
              type: 'event',
              id: ev.id,
              title: ev.title || '',
              body: `${ev.date || ''} ${ev.startTime || ''} – ${ev.endTime || ''}`,
              meta: {
                date: ev.date,
                startTime: ev.startTime,
                endTime: ev.endTime
              }
            }
          })
        }
      }
    }
  }
}

export const CalInviteeRow = {
  flow: 'x',
  align: 'center left',
  gap: 'Z1',
  padding: 'Y2 0',

  Avatar: {
    avatarType: 'initials',
    boxSize: 'B1 B1',
    src: (el, s) => el.call('avatarUrl', s.email)
  },

  Email: {
    tag: 'span',
    fontSize: 'Z2',
    color: 'subtitle',
    text: (el, s) => s.email || ''
  }
}
